import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Play, Shield, Download, BarChart3, TrendingUp, Target, AlertTriangle, History, Info, Eye, Trash2 } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { toast } from '@/hooks/use-toast';
import { HonestBacktestService, type HonestBacktestConfig, type HonestBacktestResult, type StoredHonestBacktestSimulation } from '@/services/HonestBacktestService';
import { ProgressiveBacktestService, type ProgressiveBacktestConfig, type ProgressiveBacktestResult } from '@/services/ProgressiveBacktestService';
import { type TradingFile } from '@/services/TradingPatternAnalysisService';
import { HonestBacktestResultsTable } from './HonestBacktestResultsTable';
import { HonestBacktestComparison } from './HonestBacktestComparison';
import { GrowthChart } from './GrowthChart';
import { ActualGrowthChart } from './ActualGrowthChart';

interface HonestProgressiveBacktestTabProps {
  selectedFile: TradingFile | null;
}

export const HonestProgressiveBacktestTab: React.FC<HonestProgressiveBacktestTabProps> = ({
  selectedFile
}) => {
  // Configuration state
  const [config, setConfig] = useState<HonestBacktestConfig>({
    fileId: '',
    startPosition: 100,
    endPosition: undefined,
    patternRefreshFrequency: 1,
    winThreshold: 0.55,
    minSequenceLength: 3,
    maxSequenceLength: 7,
    // Rolling window defaults (OFF for backward compatibility)
    useRollingWindow: false,
    windowSize: 50,
    fixedPatternLength: undefined,
    minFrequency: 1,
    minMatches: 1,
    returnDebug: false,
    // TP/SL ratios for realistic growth calculation
    tpRatio: 2,
    slRatio: 1
  });

  // Execution state
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number; status: string } | null>(null);
  const [currentResult, setCurrentResult] = useState<HonestBacktestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // History and comparison state
  const [simulations, setSimulations] = useState<StoredHonestBacktestSimulation[]>([]);
  const [selectedSimulation, setSelectedSimulation] = useState<StoredHonestBacktestSimulation | null>(null);
  const [regularBacktestResult, setRegularBacktestResult] = useState<ProgressiveBacktestResult | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isRunningComparison, setIsRunningComparison] = useState(false);

  // Update file ID when selected file changes
  useEffect(() => {
    if (selectedFile?.id) {
      setConfig(prev => ({ 
        ...prev, 
        fileId: selectedFile.id,
        endPosition: selectedFile.total_trades 
      }));
      loadSimulationHistory();
    }
  }, [selectedFile?.id]);

  const handleRunHonestBacktest = async () => {
    const validation = HonestBacktestService.validateConfig(config);
    if (!validation.isValid) {
      setError(validation.errors.join(', '));
      return;
    }

    setIsRunning(true);
    setError(null);
    setCurrentResult(null);
    setProgress({ current: 0, total: 100, status: 'Starting...' });

    try {
      const result = await HonestBacktestService.runBacktest(
        { ...config, fileId: selectedFile!.id },
        (progressUpdate) => setProgress(progressUpdate)
      );

      if (result.success && result.result) {
        setCurrentResult(result.result);
        loadSimulationHistory(); // Refresh history
        
        // Auto-run regular backtest for comparison
        await runRegularBacktestForComparison(result.result.config);
        
        toast.success('Honest Progressive Backtest completed successfully!');
      } else {
        setError(result.error || 'Unknown error occurred');
        toast.error(result.error || 'Failed to run backtest');
      }
    } catch (error) {
      console.error('Error running honest backtest:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to run backtest';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsRunning(false);
      setProgress(null);
    }
  };

  const runRegularBacktestForComparison = async (honestConfig: HonestBacktestConfig) => {
    setIsRunningComparison(true);
    try {
      const regularConfig: ProgressiveBacktestConfig = {
        fileId: honestConfig.fileId,
        startPosition: honestConfig.startPosition,
        maxSequenceLength: 8,
        winThreshold: honestConfig.winThreshold,
        analysisMode: 'basic'
      };

      const result = await ProgressiveBacktestService.runBacktest(regularConfig);
      if (result.success && result.result) {
        setRegularBacktestResult(result.result);
      }
    } catch (error) {
      console.error('Error running regular backtest for comparison:', error);
    } finally {
      setIsRunningComparison(false);
    }
  };

  const loadSimulationHistory = async () => {
    if (!selectedFile) return;
    
    setIsLoadingHistory(true);
    try {
      const result = await HonestBacktestService.getSimulations(selectedFile.id);
      if (result.success && result.data) {
        setSimulations(result.data);
      }
    } catch (error) {
      console.error('Error loading simulation history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleSimulationSelect = async (simulation: StoredHonestBacktestSimulation) => {
    setSelectedSimulation(simulation);
    
    // Debug logging to check pattern evolution data structure
    console.log('🔍 Debug simulation data:');
    console.log('- pattern_evolution type:', typeof simulation.pattern_evolution);
    console.log('- pattern_evolution is array:', Array.isArray(simulation.pattern_evolution));
    console.log('- pattern_evolution data:', simulation.pattern_evolution);
    console.log('- pattern_evolution keys:', simulation.pattern_evolution ? Object.keys(simulation.pattern_evolution) : 'N/A');
    
    // Convert stored simulation to HonestBacktestResult format
    const result: HonestBacktestResult = {
      config: {
        fileId: simulation.file_id,
        startPosition: simulation.start_position,
        endPosition: simulation.end_position,
        patternRefreshFrequency: simulation.pattern_refresh_frequency,
        winThreshold: simulation.win_threshold
      },
      positions: simulation.positions_data,
      summary: {
        totalPositions: simulation.total_positions,
        predictedPositions: simulation.predicted_positions,
        skippedPositions: simulation.skipped_positions,
        correctPredictions: simulation.correct_predictions,
        accuracy: simulation.accuracy,
        skipRate: simulation.skip_rate,
        finalPatternCount: simulation.summary_stats.finalPatternCount,
        initialPatternCount: simulation.summary_stats.initialPatternCount,
        averagePatternsPerPosition: simulation.summary_stats.averagePatternsPerPosition,
        maxPatterns: simulation.summary_stats.maxPatterns,
        minPatterns: simulation.summary_stats.minPatterns
      },
      // Fix pattern evolution data access - check if it's nested or direct array
      patternEvolution: Array.isArray(simulation.pattern_evolution) 
        ? simulation.pattern_evolution 
        : (simulation.pattern_evolution as any)?.evolution || [],
      processingTime: simulation.processing_time_ms
    };
    
    // Debug the converted pattern evolution
    console.log('🔍 Converted pattern evolution:', result.patternEvolution);
    console.log('- Length:', result.patternEvolution.length);
    console.log('- Sample data:', result.patternEvolution[0]);
    
    setCurrentResult(result);
  };

  const handleDeleteSimulation = async (simulationId: string) => {
    try {
      const result = await HonestBacktestService.deleteSimulation(simulationId);
      if (result.success) {
        setSimulations(prev => prev.filter(s => s.id !== simulationId));
        if (selectedSimulation?.id === simulationId) {
          setSelectedSimulation(null);
          setCurrentResult(null);
        }
        toast.success('Simulation deleted successfully');
      } else {
        toast.error(result.error || 'Failed to delete simulation');
      }
    } catch (error) {
      console.error('Error deleting simulation:', error);
      toast.error('Failed to delete simulation');
    }
  };

  const handleDownloadResults = () => {
    if (!currentResult) return;

    const csvContent = HonestBacktestService.exportToCSV(currentResult);
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `honest_backtest_${selectedFile?.filename}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Results exported to CSV');
  };

  const renderConfigurationForm = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Honest Backtest Configuration
          {config.useRollingWindow && (
            <Badge variant="secondary" className="ml-2">Rolling Window</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Configure parameters for look-ahead bias-free progressive backtest. 
          {config.useRollingWindow 
            ? `Rolling window mode: Uses last ${config.windowSize} trades for pattern analysis.`
            : 'Uses only patterns available up to the current position.'
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">What is Honest Backtest?</p>
              <p>
                Unlike regular backtests that use all patterns from the entire dataset, 
                this mode rebuilds patterns at each position using only past data. 
                This eliminates look-ahead bias and provides realistic performance estimates.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="start-position">Start Position</Label>
            <Input
              id="start-position"
              type="number"
              min="10"
              max={selectedFile?.total_trades || 1000}
              value={config.startPosition}
              onChange={(e) => setConfig(prev => ({ ...prev, startPosition: parseInt(e.target.value) || 100 }))}
            />
            <p className="text-xs text-muted-foreground">
              Position to start testing from (patterns analyzed from position 1 to start-1)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="end-position">End Position (Optional)</Label>
            <Input
              id="end-position"
              type="number"
              min={config.startPosition + 1}
              max={selectedFile?.total_trades || 1000}
              value={config.endPosition || ''}
              placeholder={`Auto: ${selectedFile?.total_trades || 'N/A'}`}
              onChange={(e) => setConfig(prev => ({ 
                ...prev, 
                endPosition: e.target.value ? parseInt(e.target.value) : undefined 
              }))}
            />
            <p className="text-xs text-muted-foreground">
              Leave empty to test until end of file
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="min-sequence">Min Sequence Length</Label>
            <Input
              id="min-sequence"
              type="number"
              min="2"
              max="8"
              value={config.minSequenceLength}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 3;
                setConfig(prev => ({ 
                  ...prev, 
                  minSequenceLength: value,
                  maxSequenceLength: Math.max(value, prev.maxSequenceLength || 7)
                }))
              }}
            />
            <p className="text-xs text-muted-foreground">
              Minimum pattern length (2-8, default: 3)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="max-sequence">Max Sequence Length</Label>
            <Input
              id="max-sequence"
              type="number"
              min="3"
              max="10"
              value={config.maxSequenceLength}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 7;
                setConfig(prev => ({ 
                  ...prev, 
                  maxSequenceLength: value,
                  minSequenceLength: Math.min(value, prev.minSequenceLength || 3)
                }))
              }}
            />
            <p className="text-xs text-muted-foreground">
              Maximum pattern length (3-10, default: 7)
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="space-y-2">
            <Label htmlFor="refresh-frequency">Pattern Refresh Frequency</Label>
            <Input
              id="refresh-frequency"
              type="number"
              min="1"
              max="50"
              value={config.patternRefreshFrequency}
              onChange={(e) => setConfig(prev => ({ 
                ...prev, 
                patternRefreshFrequency: parseInt(e.target.value) || 1 
              }))}
            />
            <p className="text-xs text-muted-foreground">
              Update patterns every N positions (1 = every position, more realistic)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="win-threshold">Win Threshold: {(config.winThreshold * 100).toFixed(0)}%</Label>
            <Input
              id="win-threshold"
              type="range"
              min="0.5"
              max="0.9"
              step="0.01"
              value={config.winThreshold}
              onChange={(e) => setConfig(prev => ({ 
                ...prev, 
                winThreshold: parseFloat(e.target.value) 
              }))}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Threshold for WIN/LOSS decisions (50%-90%)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tp-ratio">Take Profit Ratio</Label>
            <Input
              id="tp-ratio"
              type="number"
              min="0.1"
              max="10"
              step="0.1"
              value={config.tpRatio || 2}
              onChange={(e) => setConfig(prev => ({ 
                ...prev, 
                tpRatio: parseFloat(e.target.value) || 2 
              }))}
            />
            <p className="text-xs text-muted-foreground">
              Take Profit units (default: 2)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sl-ratio">Stop Loss Ratio</Label>
            <Input
              id="sl-ratio"
              type="number"
              min="0.1"
              max="10"
              step="0.1"
              value={config.slRatio || 1}
              onChange={(e) => setConfig(prev => ({ 
                ...prev, 
                slRatio: parseFloat(e.target.value) || 1 
              }))}
            />
            <p className="text-xs text-muted-foreground">
              Stop Loss units (default: 1)
            </p>
          </div>
        </div>

        {/* Rolling Window Configuration */}
        <div className="border-t pt-6">
          <div className="flex items-center gap-2 mb-4">
            <input
              type="checkbox"
              id="rolling-window"
              checked={config.useRollingWindow}
              onChange={(e) => setConfig(prev => ({ 
                ...prev, 
                useRollingWindow: e.target.checked 
              }))}
              className="h-4 w-4"
            />
            <Label htmlFor="rolling-window" className="text-sm font-medium">
              Use Rolling Window Mode
            </Label>
          </div>
          
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg mb-4">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-blue-600 mt-0.5" />
              <div className="text-sm text-gray-700">
                <p className="font-medium mb-1">Rolling Window Mode</p>
                <p>
                  Instead of accumulating patterns from all past data, this mode uses only the last N trades
                  to build patterns for each prediction. This adapts faster to changing market conditions
                  but may have lower prediction coverage.
                </p>
              </div>
            </div>
          </div>

          {config.useRollingWindow && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="window-size">Window Size</Label>
                  <Input
                    id="window-size"
                    type="number"
                    min="10"
                    max="200"
                    value={config.windowSize}
                    onChange={(e) => setConfig(prev => ({ 
                      ...prev, 
                      windowSize: parseInt(e.target.value) || 50 
                    }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Number of recent trades to use (10-200, default: 50)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fixed-pattern-length">Fixed Pattern Length (Optional)</Label>
                  <Input
                    id="fixed-pattern-length"
                    type="number"
                    min="2"
                    max="10"
                    value={config.fixedPatternLength || ''}
                    placeholder="Auto (3-7)"
                    onChange={(e) => setConfig(prev => ({ 
                      ...prev, 
                      fixedPatternLength: e.target.value ? parseInt(e.target.value) : undefined 
                    }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Use single pattern length instead of range
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="min-frequency">Min Frequency</Label>
                  <Input
                    id="min-frequency"
                    type="number"
                    min="1"
                    max="10"
                    value={config.minFrequency}
                    onChange={(e) => setConfig(prev => ({ 
                      ...prev, 
                      minFrequency: parseInt(e.target.value) || 1 
                    }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Minimum pattern frequency to use
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="min-matches">Min Matches</Label>
                  <Input
                    id="min-matches"
                    type="number"
                    min="1"
                    max="5"
                    value={config.minMatches}
                    onChange={(e) => setConfig(prev => ({ 
                      ...prev, 
                      minMatches: parseInt(e.target.value) || 1 
                    }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Minimum matching patterns required for prediction
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="return-debug"
                    checked={config.returnDebug}
                    onChange={(e) => setConfig(prev => ({ 
                      ...prev, 
                      returnDebug: e.target.checked 
                    }))}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="return-debug" className="text-sm">
                    Include Debug Information
                  </Label>
                </div>
              </div>
            </div>
          )}
        </div>

        <Button 
          onClick={handleRunHonestBacktest} 
          disabled={isRunning || !selectedFile}
          className="w-full"
        >
          <Play className="h-4 w-4 mr-2" />
          {isRunning ? 'Running Honest Backtest...' : 'Start Honest Backtest'}
        </Button>

        {isRunning && progress && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>{progress.status}</span>
              <span>{progress.current}/{progress.total}</span>
            </div>
            <Progress value={(progress.current / progress.total) * 100} />
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );

  const renderResults = () => {
    if (!currentResult) return null;

    const { summary, patternEvolution } = currentResult;

    const performanceData = currentResult.positions.map((pos, index) => ({
      position: pos.position,
      accuracy: index === 0 ? 0 : 
        (currentResult.positions.slice(0, index + 1)
          .filter(p => p.prediction !== 'SKIP')
          .reduce((acc, p) => acc + (
            (p.prediction === 'WIN' && p.actualResult === 1) ||
            (p.prediction === 'LOSS' && p.actualResult === 0) ? 1 : 0
          ), 0) / 
        currentResult.positions.slice(0, index + 1)
          .filter(p => p.prediction !== 'SKIP').length) * 100,
      patternCount: pos.currentPatternCount
    }));

    return (
      <Tabs defaultValue="summary" className="space-y-6">
        <TabsList className="grid grid-cols-7 w-full">
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="detailed">Detailed Results</TabsTrigger>
          <TabsTrigger value="growth-performance">Growth Analytics</TabsTrigger>
          <TabsTrigger value="market-growth">Market Growth</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="evolution">Pattern Evolution</TabsTrigger>
          {regularBacktestResult && <TabsTrigger value="comparison">vs Regular</TabsTrigger>}
        </TabsList>

        <TabsContent value="summary" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-primary" />
                  <div>
                    <p className="text-sm font-medium">Honest Accuracy</p>
                    <p className="text-2xl font-bold">{summary.accuracy.toFixed(1)}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-green-500" />
                  <div>
                    <p className="text-sm font-medium">Predictions Made</p>
                    <p className="text-2xl font-bold">{summary.predictedPositions}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-blue-500" />
                  <div>
                    <p className="text-sm font-medium">Skip Rate</p>
                    <p className="text-2xl font-bold">{summary.skipRate.toFixed(1)}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-purple-500" />
                  <div>
                    <p className="text-sm font-medium">Final Patterns</p>
                    <p className="text-2xl font-bold">{summary.finalPatternCount}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex gap-4">
            <Button onClick={handleDownloadResults} variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export Results
            </Button>
            {isRunningComparison && (
              <Badge variant="secondary" className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                Running comparison...
              </Badge>
            )}
          </div>
        </TabsContent>

        <TabsContent value="detailed" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Position-by-Position Results</CardTitle>
              <CardDescription>
                Detailed breakdown showing patterns used and prediction reasoning
              </CardDescription>
            </CardHeader>
            <CardContent>
              <HonestBacktestResultsTable 
                positions={currentResult.positions}
                maxRows={50}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="growth-performance" className="space-y-6">
          <GrowthChart 
            positionsData={currentResult.positions.map(position => ({
              position: position.position,
              prediction: position.prediction,
              actualResult: position.actualResult,
              isCorrect: (position.prediction === 'WIN' && position.actualResult === 1) || 
                        (position.prediction === 'LOSS' && position.actualResult === 0)
            }))}
            tpRatio={config.tpRatio || 2}
            slRatio={config.slRatio || 1}
          />
        </TabsContent>

        <TabsContent value="market-growth" className="space-y-6">
          <ActualGrowthChart 
            positionsData={currentResult.positions.map(position => ({
              position: position.position,
              prediction: position.prediction,
              actualResult: position.actualResult,
              isCorrect: (position.prediction === 'WIN' && position.actualResult === 1) || 
                        (position.prediction === 'LOSS' && position.actualResult === 0)
            }))}
            tpRatio={config.tpRatio || 2}
            slRatio={config.slRatio || 1}
          />
        </TabsContent>

        <TabsContent value="performance" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Performance Evolution</CardTitle>
              <CardDescription>
                How accuracy evolves as more patterns become available
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={performanceData.filter((_, i) => i % 10 === 0)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="position" />
                    <YAxis domain={[0, 100]} />
                    <RechartsTooltip 
                      formatter={(value: number) => [`${value.toFixed(1)}%`, 'Accuracy']}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="accuracy" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="evolution" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Pattern Evolution</CardTitle>
              <CardDescription>
                How pattern count grows throughout the backtest
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={patternEvolution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="position" />
                    <YAxis />
                    <RechartsTooltip />
                    <Line 
                      type="monotone" 
                      dataKey="patternCount" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={2}
                      name="Pattern Count"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {regularBacktestResult && (
          <TabsContent value="comparison" className="space-y-6">
            <HonestBacktestComparison 
              honestResult={currentResult}
              regularResult={regularBacktestResult}
            />
          </TabsContent>
        )}
      </Tabs>
    );
  };

  const renderHistoryPanel = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-5 w-5" />
          Simulation History
        </CardTitle>
        <CardDescription>
          Previous honest backtest runs for this file
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoadingHistory ? (
          <div className="text-center py-4">
            <div className="inline-block w-4 h-4 border-2 border-primary border-r-transparent rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground mt-2">Loading history...</p>
          </div>
        ) : simulations.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No previous simulations found. Run your first honest backtest above.
          </p>
        ) : (
          <div className="space-y-2">
            {simulations.map((simulation) => (
              <div 
                key={simulation.id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/5 cursor-pointer"
                onClick={() => handleSimulationSelect(simulation)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {simulation.accuracy.toFixed(1)}% accuracy
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      {simulation.predicted_positions} predictions
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(simulation.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Pos {simulation.start_position}-{simulation.end_position || 'end'} | 
                    Threshold {(simulation.win_threshold * 100).toFixed(0)}%
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSimulationSelect(simulation);
                    }}
                  >
                    <Eye className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSimulation(simulation.id);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {renderConfigurationForm()}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {renderResults()}
          </div>
          <div>
            {renderHistoryPanel()}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};